import React, {
  type ComponentType,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { FormattedMessage } from "react-intl";
import { Alert, Col, Row } from "reactstrap";
import { ShortCodeVerify } from "src/numbers/forms/short-code-form/ShortCodeVerify";
import { useShortCodeProviders } from "src/numbers/hooks/useShortCodeProviders";
import {
  type IPhoneNoInputProps,
  PhoneNoInput,
} from "src/shared/forms/inputs/PhoneNoInputNew";
import { SHORT_CODE_REGEX } from "src/shared/forms/inputs/phoneNoValidationRegex";
import {
  type ISelectInputProps,
  SelectInput,
} from "src/shared/forms/inputs/SelectInput";
import {
  Validation,
  type ValidationProps,
} from "src/shared/forms/inputs/Validation";

export interface ShortCodeFormProps {
  countryCode: string;
  providerInputId: string;
  numberInputId: string;
  disabled?: boolean;
  SelectComponent?: ComponentType<
    Pick<
      ISelectInputProps,
      "inputID" | "label" | "isRequired" | "disabled" | "children"
    >
  >;
  PhoneNoComponent?: ComponentType<
    Pick<
      IPhoneNoInputProps,
      | "inputID"
      | "label"
      | "isRequired"
      | "disabled"
      | "validationDisabled"
      | "phoneRegex"
      | "maxLength"
    >
  >;
  ValidationComponent?: ComponentType<
    Pick<ValidationProps, "inputID" | "validate">
  >;
  providerId: string | number | undefined;
  mobileVirtualNumber: string | undefined;
  children?: ReactNode;
}

export const ShortCodeForm = ({
  SelectComponent = SelectInput,
  PhoneNoComponent = PhoneNoInput,
  ValidationComponent = Validation,
  countryCode,
  providerInputId,
  numberInputId,
  providerId,
  mobileVirtualNumber,
  disabled = false,
  children,
}: ShortCodeFormProps) => {
  const { data: providers } = useShortCodeProviders(countryCode, {
    enabled: !disabled,
  });

  const [isNumberVerified, setIsNumberVerified] = useState(false);
  const handleNumberVerifySuccess = useCallback(() => {
    setIsNumberVerified(true);
  }, []);

  useEffect(() => {
    setIsNumberVerified(false);
  }, [providerId, mobileVirtualNumber]);

  const isNumberVerifiedValidation = useCallback(() => {
    if (!isNumberVerified) {
      return (
        <Alert color="warning">
          <span className="fas fa-exclamation-circle" />{" "}
          <FormattedMessage id="numbers.form.shortCode.verificationRequired" />
        </Alert>
      );
    }
    return null;
  }, [isNumberVerified]);

  return (
    <>
      <Row>
        <Col sm={12}>
          <SelectComponent
            inputID={providerInputId}
            label={<FormattedMessage id="numbers.form.shortCode.provider" />}
            isRequired={!disabled}
            disabled={disabled}
          >
            {providers
              ? providers.map((provider) => (
                  <option key={provider.providerId} value={provider.providerId}>
                    {provider.providerName}
                  </option>
                ))
              : null}
          </SelectComponent>
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          <Row>
            <Col sm={12}>
              <PhoneNoComponent
                inputID={numberInputId}
                label={
                  <FormattedMessage id="numbers.form.shortCode.mobileVirtualNumber" />
                }
                isRequired={!disabled}
                disabled={disabled}
                validationDisabled={disabled}
                phoneRegex={SHORT_CODE_REGEX}
                maxLength={32}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      {children}
      <Row>
        <Col sm={12}>
          <ShortCodeVerify
            countryCode={countryCode}
            providerId={providerId}
            mobileVirtualNumber={mobileVirtualNumber}
            disabled={!providerId || !mobileVirtualNumber || isNumberVerified}
            onNumberVerifySuccess={handleNumberVerifySuccess}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          <ValidationComponent
            inputID="shortCodeNumberVerified"
            validate={isNumberVerifiedValidation}
          />
        </Col>
      </Row>
    </>
  );
};
